import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Checkbox } from "../../components/ui/checkbox"

type PaymentTermFormData = {
  id?: number
  name: string
  days: string
  active: boolean
}

type PaymentTermFormProps = {
  initialValues: PaymentTermFormData
  submitUrl: string
  submitLabel: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
}

function ErrorText({ value }: { value?: string[] }) {
  if (!value?.length) return null
  return <p className="text-xs text-rose-400">{value[0]}</p>
}

export function PaymentTermForm({ initialValues, submitUrl, submitLabel, method, errors }: PaymentTermFormProps) {
  const form = useForm({
    payment_term: {
      name: initialValues.name ?? "",
      days: initialValues.days ?? "",
      active: initialValues.active ?? true
    }
  })

  const data = form.data.payment_term

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (method === "patch") {
      form.patch(submitUrl)
      return
    }
    form.post(submitUrl)
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Nome</Label>
          <Input
            id="name"
            required
            placeholder="Ex: À vista, 30 dias, 60 dias..."
            value={data.name}
            onChange={(e) => form.setData("payment_term", { ...data, name: e.target.value })}
          />
          <ErrorText value={errors?.name} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="days">Dias</Label>
          <Input
            id="days"
            type="number"
            required
            min={0}
            step={1}
            placeholder="0"
            value={data.days}
            onChange={(e) => form.setData("payment_term", { ...data, days: e.target.value })}
          />
          <p className="text-xs text-slate-500">
            Número de dias para vencimento. 0 = à vista.
          </p>
          <ErrorText value={errors?.days} />
        </div>
        <div className="flex items-center gap-3 md:col-span-2">
          <Checkbox
            id="active"
            checked={data.active}
            onChange={(e) => form.setData("payment_term", { ...data, active: e.target.checked })}
          />
          <Label htmlFor="active" className="cursor-pointer">
            Ativa (aparece no dropdown de criação de notas)
          </Label>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Link
          href={initialValues.id ? "/datas-pagamento" : "/datas-pagamento"}
          className="inline-flex h-10 items-center justify-center rounded-md border border-slate-700 bg-transparent px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-800"
        >
          Cancelar
        </Link>
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  )
}
